import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';

const CARD_MIN_WIDTH = 340;
const CARD_LIST_MAX_HEIGHT = 320;

export const styleSheet = StyleSheet.create({
	// No padding — the page `layout` already insets it; adding more would double the gutter.
	container: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minHeight: 0,
		overflowY: 'auto',
	},
	adminBar: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.xs,
	},
	adminBarLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
	},
	grid: {
		display: 'grid',
		gap: THEME.spacing.md,
		gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
		[THEME.queries.sm]: {
			gridTemplateColumns: '1fr',
		},
	},
	card: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		minHeight: 120,
		minWidth: 0,
		overflow: 'hidden',
	},
	cardHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderBottom: THEME.common.border,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	cardRepIdentity: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		minWidth: 0,
	},
	cardRepName: {
		...THEME.common.truncate,
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		maxWidth: '100%',
	},
	cardCount: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	cardHeaderActions: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		gap: THEME.spacing.xs,
	},
	cardHeaderIcon: {
		flexShrink: 0,
		height: 24,
		width: 24,
	},
	spin: {
		animationName: {
			'0%': { transform: 'rotate(0deg)' },
			'100%': { transform: 'rotate(360deg)' },
		},
		animationDuration: '0.8s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'linear',
	},
	filters: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.sm,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	filterRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	filterLabel: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		width: 64,
	},
	filterRangeInput: {
		minWidth: 0,
		width: '100%',
	},
	filterRangeSep: {
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	cardBody: {
		display: 'flex',
		flexDirection: 'column',
		maxHeight: CARD_LIST_MAX_HEIGHT,
		minHeight: 0,
		overflowY: 'auto',
	},
	cardState: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		justifyContent: 'center',
		minHeight: 96,
		padding: THEME.spacing.md,
		textAlign: 'center',
	},
	cardPlaceholder: {
		minHeight: 96,
	},
	row: {
		alignItems: 'flex-start',
		background: 'transparent',
		border: 'none',
		borderBottom: THEME.common.border,
		borderRadius: 0,
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		textAlign: 'left',
		width: '100%',
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	rowSelected: {
		backgroundColor: THEME.colors.hover.light,
		boxShadow: `inset 3px 0 0 ${THEME.colors.primary.main}`,
	},
	rowTopLine: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		width: '100%',
	},
	rowSubject: {
		...THEME.common.truncate,
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		minWidth: 0,
	},
	rowMeta: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: THEME.typography.fontSizes.xs,
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		width: '100%',
	},
	rowDuration: {
		color: THEME.colors.text.main,
		flexShrink: 0,
		fontVariantNumeric: 'tabular-nums',
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	cardFooter: {
		alignItems: 'center',
		borderTop: THEME.common.border,
		display: 'flex',
		flexShrink: 0,
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	loadMoreButton: {
		flex: 1,
	},
	spinnerState: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		minHeight: 96,
	},
});
